import * as React from 'react'

type TBaseProps = {
  isOpen: boolean
}

/**
 * Basic reusable hook to control a modal.
 * Returns currently set `modalElement` instance,
 * the `presentModal` setter and
 * the `dismissModal` to reset the active modal (sets `modalElement` to `null`)
 */
export const useModalControl = <TModalProps extends TBaseProps>(
  ModalComponent: React.ComponentType<TModalProps>,
) => {
  const [modalElement, setModalElement] = React.useState<React.ReactNode>(null)

  const dismissModal = React.useCallback(() => setModalElement(null), [])

  const presentModal = React.useCallback(
    (modalProps: Omit<PropsOf<typeof ModalComponent>, 'isOpen'>) => {
      setModalElement(
        React.createElement(ModalComponent, {
          ...modalProps,
          isOpen: true,
        } as TModalProps),
      )
    },
    [ModalComponent],
  )

  return [modalElement, presentModal, dismissModal] as const
}
